import React, { useState, useEffect, useRef } from "react";
import "./createServiceModal.css";
import { FiClock, FiTag, FiTool } from "react-icons/fi";
import { IoChevronDownOutline, IoCheckmarkSharp } from "react-icons/io5";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";

export default function CreateServiceModal({ onClose, onServiceCreated }) {
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [duration, setDuration] = useState(15);
  const [customDuration, setCustomDuration] = useState("");
  const [numServices, setNumServices] = useState("Unlimited");
  const [customNum, setCustomNum] = useState("");

  const [openDropdown, setOpenDropdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const modalRef = useRef(null);

  const durationOptions = [
    { label: "15 minutes", value: 15 },
    { label: "30 minutes", value: 30 },
    { label: "1 hour", value: 60 },
    { label: "2 hours", value: 120 },
    { label: "Other", value: "Other" },
  ];

  const numOptions = ["10", "20", "Unlimited", "Other"];

  const toggleDropdown = (type) => {
    if (loading) return;
    setOpenDropdown((prev) => (prev === type ? null : type));
  };

  const handleSelect = (type, value) => {
    if (loading) return;

    if (type === "duration") {
      setDuration(value);
      if (value !== "Other") setCustomDuration("");
    }

    if (type === "services") {
      setNumServices(value);
      if (value !== "Other") setCustomNum("");
    }

    setOpenDropdown(null);
  };

  const validateNumberInput = (value, fieldName) => {
    const numValue = parseFloat(value);

    if (fieldName === "price") {
      if (Number.isNaN(numValue) || numValue <= 0) {
        return "Please enter a valid positive price.";
      }
    }

    if (fieldName === "customDuration") {
      if (Number.isNaN(numValue) || numValue <= 0) {
        return "Duration must be a positive number.";
      }
      if (numValue > 1440) {
        return "Duration cannot exceed 24 hours.";
      }
    }

    if (fieldName === "customNum") {
      if (Number.isNaN(numValue) || numValue <= 0) {
        return "Number of services must be positive.";
      }
      if (numValue > 9999) {
        return "Maximum 9999 services allowed.";
      }
    }

    return "";
  };

  const validateForm = () => {
    const newErrors = {};

    if (!name.trim()) {
      newErrors.name = "Service name is required.";
    }

    const priceError = validateNumberInput(price, "price");
    if (priceError) {
      newErrors.price = priceError;
    }

    if (!description.trim()) {
      newErrors.description = "Service description is required.";
    }

    if (duration === "Other") {
      if (!customDuration) {
        newErrors.customDuration = "Please enter custom duration.";
      } else {
        const durationError = validateNumberInput(
          customDuration,
          "customDuration"
        );
        if (durationError) {
          newErrors.customDuration = durationError;
        }
      }
    }

    if (numServices === "Other") {
      if (!customNum) {
        newErrors.customNum = "Please enter number of services.";
      } else {
        const numError = validateNumberInput(customNum, "customNum");
        if (numError) {
          newErrors.customNum = numError;
        }
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fix the highlighted errors.");
      return false;
    }

    return true;
  };

  const handlePriceChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setPrice(value);
    }
  };

  const handleCustomDurationChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setCustomDuration(value);
    }
  };

  const handleCustomNumChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setCustomNum(value);
    }
  };

  const handleCreate = async () => {
    if (loading) return;
    if (!validateForm()) return;

    const finalDuration =
      duration === "Other"
        ? parseInt(customDuration, 10) || 30
        : parseInt(duration, 10);

    const finalServiceCount =
      numServices === "Other"
        ? parseInt(customNum, 10) || 1
        : numServices === "Unlimited" || !numServices
          ? "Unlimited"
          : parseInt(numServices, 10);

    setLoading(true);

    try {
      const newService = {
        name: name.trim(),
        description: description.trim(),
        duration: finalDuration,
        price: parseFloat(price),
        service_count: finalServiceCount,
        currency: "USD",
        time_unit: "minutes",
        on_off: false,
        id: Date.now(),
      };

      if (onServiceCreated) {
        await Promise.resolve(onServiceCreated(newService));
      }

      toast.success("Service created successfully!");
      onClose();
    } catch (error) {
      console.error("Error creating service:", error);
      toast.error(error?.message || "Failed to create service.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (loading) return;
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [loading]);

  return (
    <AnimatePresence>
      <motion.div
        className="create-service-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => {
          if (loading) return;
          if (e.target.classList.contains("create-service-overlay")) onClose();
        }}
      >
        <motion.div
          ref={modalRef}
          className="create-service-modal"
          initial={{ opacity: 0, scale: 0.8, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.85, y: 30 }}
          transition={{ type: "spring", stiffness: 150, damping: 18 }}
        >
          <div className="create-service-header">
            <button
              className="create-service-back-btn"
              onClick={onClose}
              disabled={loading}
            >
              ← Back
            </button>
            <div className="create-service-title-section">
              <h2>Create Service</h2>
              <p>Add details about your new service</p>
            </div>
          </div>

          <div className="create-service-form">
            <div className="create-service-input-row">
              <div className="create-service-input-group">
                <label>Service Name</label>
                <input
                  type="text"
                  placeholder="Type Service name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={errors.name ? "invalid" : ""}
                  disabled={loading}
                />
                {errors.name && (
                  <span className="input-error-text">{errors.name}</span>
                )}
              </div>

              <div className="create-service-input-group create-service-dropdown">
                <label>Duration</label>
                <div
                  className={`create-service-dropdown-field ${
                    errors.customDuration ? "invalid" : ""
                  } ${loading ? "disabled" : ""}`}
                  onClick={() => toggleDropdown("duration")}
                >
                  <FiClock className="create-service-icon" />
                  <span className="create-service-dropdown-text">
                    {duration === "Other"
                      ? `${customDuration || "Custom"} min`
                      : `${duration} min`}
                  </span>
                  <IoChevronDownOutline className="create-service-chevron" />
                </div>

                <AnimatePresence>
                  {openDropdown === "duration" && !loading && (
                    <motion.div
                      className="create-service-dropdown-menu"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.2 }}
                    >
                      {durationOptions.map((opt) => (
                        <div
                          key={opt.value}
                          className="create-service-dropdown-item"
                          onClick={() => handleSelect("duration", opt.value)}
                        >
                          <span>{opt.label}</span>
                          {duration === opt.value ? (
                            <div className="create-service-success-check">
                              <IoCheckmarkSharp />
                            </div>
                          ) : (
                            <div className="create-service-empty-circle"></div>
                          )}
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                {duration === "Other" && (
                  <>
                    <input
                      type="text"
                      className={`create-service-custom-input ${
                        errors.customDuration ? "invalid" : ""
                      }`}
                      placeholder="Enter minutes (positive number)"
                      value={customDuration}
                      onChange={handleCustomDurationChange}
                      disabled={loading}
                    />
                    {errors.customDuration && (
                      <span className="input-error-text">
                        {errors.customDuration}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="create-service-input-row">
              <div className="create-service-input-group">
                <label>Service Price</label>
                <div
                  className={`create-service-input-icon-field ${
                    errors.price ? "invalid" : ""
                  }`}
                >
                  <FiTag className="create-service-icon" />
                  <input
                    type="text"
                    placeholder="30"
                    value={price}
                    onChange={handlePriceChange}
                    className="create-service-number-input"
                    disabled={loading}
                  />
                </div>
                {errors.price && (
                  <span className="input-error-text">{errors.price}</span>
                )}
              </div>

              <div className="create-service-input-group create-service-dropdown">
                <label>№ of Services</label>
                <div
                  className={`create-service-dropdown-field ${
                    errors.customNum ? "invalid" : ""
                  } ${loading ? "disabled" : ""}`}
                  onClick={() => toggleDropdown("services")}
                >
                  <FiTool className="create-service-icon" />
                  <span className="create-service-dropdown-text">
                    {numServices === "Other"
                      ? customNum || "Custom"
                      : numServices || "Unlimited"}
                  </span>
                  <IoChevronDownOutline className="create-service-chevron" />
                </div>

                <AnimatePresence>
                  {openDropdown === "services" && !loading && (
                    <motion.div
                      className="create-service-dropdown-menu"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.2 }}
                    >
                      {numOptions.map((opt) => (
                        <div
                          key={opt}
                          className="create-service-dropdown-item"
                          onClick={() => handleSelect("services", opt)}
                        >
                          <span>{opt}</span>
                          {(numServices || "Unlimited") === opt ? (
                            <div className="create-service-success-check">
                              <IoCheckmarkSharp />
                            </div>
                          ) : (
                            <div className="create-service-empty-circle"></div>
                          )}
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                {numServices === "Other" && (
                  <>
                    <input
                      type="text"
                      className={`create-service-custom-input ${
                        errors.customNum ? "invalid" : ""
                      }`}
                      placeholder="Enter number"
                      value={customNum}
                      onChange={handleCustomNumChange}
                      disabled={loading}
                    />
                    {errors.customNum && (
                      <span className="input-error-text">
                        {errors.customNum}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="create-service-input-group create-service-textarea">
              <label>Service Description</label>
              <textarea
                placeholder="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={errors.description ? "invalid" : ""}
                disabled={loading}
              />
              {errors.description && (
                <span className="input-error-text">{errors.description}</span>
              )}
            </div>
          </div>

          <div className="create-service-footer">
            <button
              className="create-service-btn"
              onClick={handleCreate}
              disabled={loading}
            >
              {loading ? "Creating..." : "Create Service"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}